/*
  NAAMANE lichte effecten (eigen code, klein gehouden voor mobiel):
  - nm-split: gescheurde split-hero, volgt de muis of een sleepbeweging
  - nm-hero: hero met slides (BABYBOO-stijl), infaden, puntjes en swipen
  - [data-nm-scramble]: kop "decodeert" één keer als hij in beeld komt
  - [data-nm-reveal]: rustig infaden en kleur aan bij scrollen
  Alles staat uit bij "verminder beweging".
*/
const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const clamp = (v, a, b) => Math.min(b, Math.max(a, v));

const CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789#%&*';
function scramble(el) {
  if (reduced || el._scrambling) return;
  const text = el.dataset.nmText || el.textContent;
  el.dataset.nmText = text;
  el._scrambling = true;
  let frame = 0;
  const total = 18;
  const tick = () => {
    frame += 1;
    const p = frame / total;
    el.textContent = [...text]
      .map((ch, i) => (ch === ' ' || i / text.length < p ? ch : CHARS[(Math.random() * CHARS.length) | 0]))
      .join('');
    if (frame < total) requestAnimationFrame(tick);
    else {
      el.textContent = text;
      el._scrambling = false;
    }
  };
  requestAnimationFrame(tick);
}

const io = new IntersectionObserver(
  (entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      const el = entry.target;
      if (el.hasAttribute('data-nm-scramble')) scramble(el);
      el.classList.add('is-in');
      io.unobserve(el);
    });
  },
  { threshold: 0.25 }
);

function watch(root = document) {
  root.querySelectorAll('[data-nm-scramble], [data-nm-reveal], [data-nm-color]').forEach((el, i) => {
    if (el._watched) return;
    el._watched = true;
    if (el.hasAttribute('data-nm-reveal')) el.style.setProperty('--nm-delay', `${(i % 4) * 0.06}s`);
    if (el.hasAttribute('data-nm-scramble')) el.addEventListener('mouseenter', () => scramble(el));
    io.observe(el);
  });
}

class NmSplit extends HTMLElement {
  connectedCallback() {
    if (this._ready) return;
    this._ready = true;
    this.base = parseFloat(getComputedStyle(this).getPropertyValue('--split')) || 52;
    if (reduced) return;
    this.pos = this.base;
    this.target = this.base;
    this.tick = this.tick.bind(this);

    if (window.matchMedia('(hover: hover)').matches) {
      this.addEventListener('pointermove', (e) => {
        const r = this.getBoundingClientRect();
        this.target = clamp(((e.clientX - r.left) / r.width) * 100, 8, 92);
        this.touched();
      });
      this.addEventListener('pointerleave', () => { this.target = this.base; this.run(); });
    } else {
      let x0 = null;
      let start = 0;
      this.addEventListener('touchstart', (e) => { x0 = e.touches[0].clientX; start = this.target; }, { passive: true });
      this.addEventListener('touchmove', (e) => {
        if (x0 === null) return;
        const dx = e.touches[0].clientX - x0;
        if (Math.abs(dx) < 6) return;
        this.target = clamp(start + (dx / this.clientWidth) * 100, 6, 94);
        this.touched();
      }, { passive: true });
      this.addEventListener('touchend', () => { x0 = null; }, { passive: true });
    }
    // korte hint dat je de scheur kunt bewegen
    setTimeout(() => { if (!this.classList.contains('is-touched')) { this.target = this.base - 7; this.run(); } }, 900);
    setTimeout(() => { if (!this.classList.contains('is-touched')) { this.target = this.base; this.run(); } }, 1700);
  }

  disconnectedCallback() { this._ready = false; }

  touched() {
    if (!this.classList.contains('is-touched')) this.classList.add('is-touched');
    this.run();
  }

  run() {
    if (!this._raf) this._raf = requestAnimationFrame(this.tick);
  }

  tick() {
    this._raf = null;
    const d = this.target - this.pos;
    this.pos += d * 0.12;
    this.style.setProperty('--split', `${this.pos.toFixed(2)}%`);
    if (Math.abs(d) > 0.05) this.run();
  }
}

class NmHero extends HTMLElement {
  connectedCallback() {
    if (this._ready) return;
    this._ready = true;
    this.slides = [...this.querySelectorAll('.nm-hero__slide')];
    this.dots = [...this.querySelectorAll('.nm-hero__dot')];
    this.index = 0;
    if (this.slides.length < 2) return;
    this.delay = Number(this.dataset.autoplay || 0) * 1000;
    this.dots.forEach((d) => d.addEventListener('click', () => this.go(Number(d.dataset.go))));
    let x0 = null;
    this.addEventListener('touchstart', (e) => { x0 = e.touches[0].clientX; }, { passive: true });
    this.addEventListener('touchend', (e) => {
      if (x0 === null) return;
      const dx = e.changedTouches[0].clientX - x0;
      x0 = null;
      if (Math.abs(dx) > 45) this.go(this.index + (dx < 0 ? 1 : -1));
    }, { passive: true });
    this.addEventListener('mouseenter', () => { this.paused = true; clearTimeout(this._t); });
    this.addEventListener('mouseleave', () => { this.paused = false; this.schedule(); });
    this.schedule();
  }

  disconnectedCallback() { clearTimeout(this._t); this._ready = false; }

  schedule() {
    clearTimeout(this._t);
    if (reduced || !this.delay || this.paused) return;
    this._t = setTimeout(() => this.go(this.index + 1), this.delay);
  }

  go(to) {
    const n = this.slides.length;
    const next = (to + n) % n;
    this.slides.forEach((s, i) => {
      const on = i === next;
      s.classList.toggle('is-active', on);
      if (on) { s.removeAttribute('aria-hidden'); s.removeAttribute('tabindex'); }
      else { s.setAttribute('aria-hidden', 'true'); s.setAttribute('tabindex', '-1'); }
    });
    this.dots.forEach((d, i) => d.classList.toggle('is-active', i === next));
    this.index = next;
    this.schedule();
  }
}

if (!customElements.get('nm-hero')) customElements.define('nm-hero', NmHero);

if (!customElements.get('nm-split')) customElements.define('nm-split', NmSplit);
watch();
document.addEventListener('shopify:section:load', (e) => watch(e.target));
